import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play, Pause, Square, Upload, Download, Plus, Trash2, Volume2, X,
  Loader2, Scissors, Music, Zap, Disc3, SkipBack, Mic, MessageSquare,
  AudioWaveform, Layers, Keyboard, Wand2, Sparkles, Repeat, Eraser, Undo2, Redo2, Copy,
  Mail, ShoppingCart
} from 'lucide-react';
import AICreditsPanel from './AICreditsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#f97316','#eab308','#fbbf24','#facc15','#fde047','#ec4899','#22c55e','#06b6d4','#8b5cf6','#ef4444'];
const KEYS = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
const DT = { volume: 80, pan: 0, muted: false, solo: false, startTime: 0, pitch: 0, sourceBpm: null, sourceKey: null, effects: { reverb: false, delay: false, eq: false, comp: false } };

/* ═══════════ KAZAJAH DAW UNIFIED ═══════════ */
export default function KazajahDAW() {
  const [tracks, setTracks] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [bpm, setBpm] = useState(120);
  const [timelineKey, setTimelineKey] = useState(0); // 0..11 (C..B)
  const [autoFit, setAutoFit] = useState(true); // when true new tracks adopt timeline BPM/Key
  const [zoom, setZoom] = useState(60);
  const [master, setMaster] = useState({ loudness: 95, limiter: 85, stereoWidth: 100, eq: { low: 0, mid: 0, high: 0 } });
  const [pitch, setPitch] = useState(0);
  const [tempo, setTempo] = useState(100);
  const [exporting, setExporting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [panel, setPanel] = useState(null); // 'master','stems','ai','voice','beats'
  const [stemsLoading, setStemsLoading] = useState(false);
  const [stemsProg, setStemsProg] = useState(0);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const [splitMode, setSplitMode] = useState(false);
  const [eraseMode, setEraseMode] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [eraseWindow, setEraseWindow] = useState(0.5);
  const [selection, setSelection] = useState(null);          // for clip ops: {trackIdx, startSec, endSec}
  const [regionSelection, setRegionSelection] = useState(null); // for loop+erase: {startSec, endSec}
  const [timelineUnit, setTimelineUnit] = useState('bars');  // 'bars' or 'sec'

  const ctxRef = useRef(null);
  const srcsRef = useRef([]);
  const animRef = useRef(null);
  const t0Ref = useRef(0);
  const fileRef = useRef(null);
  const stemsRef = useRef(null);
  const recRef = useRef(null);
  const recChunks = useRef([]);
  const tlRef = useRef(null);

  // ── Undo/Redo history (last 30 states of `tracks`) ────────────────────
  const historyRef = useRef({ past: [], future: [] });
  const skipHistoryRef = useRef(false);
  const prevTracksRef = useRef([]);

  // Push the previous tracks state to history right before tracks change
  useEffect(() => {
    if (skipHistoryRef.current) {
      skipHistoryRef.current = false;
      prevTracksRef.current = tracks;
      return;
    }
    if (prevTracksRef.current !== tracks && prevTracksRef.current.length + tracks.length > 0) {
      historyRef.current.past.push(prevTracksRef.current);
      if (historyRef.current.past.length > 30) historyRef.current.past.shift();
      historyRef.current.future = [];
    }
    prevTracksRef.current = tracks;
  }, [tracks]);

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.past.length === 0) return;
    const prev = h.past.pop();
    h.future.push(prevTracksRef.current);
    skipHistoryRef.current = true;
    setTracks(prev);
  }, []);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (h.future.length === 0) return;
    const next = h.future.pop();
    h.past.push(prevTracksRef.current);
    skipHistoryRef.current = true;
    setTracks(next);
  }, []);

  // Keyboard shortcuts: Ctrl/Cmd + Z / Shift+Z / D (duplicate)
  // duplicateTrack is declared later — use a ref to dereference at call time
  const duplicateTrackRef = useRef(() => {});
  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); redo(); }
      else if (k === 'd') { e.preventDefault(); duplicateTrackRef.current(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const getCtx = useCallback(() => {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    if (ctxRef.current.state === 'suspended') ctxRef.current.resume();
    return ctxRef.current;
  }, []);

  const dur = Math.max(30, ...tracks.map(t => (t.startTime || 0) + (t.buffer?.duration || 0)));
  const px = zoom;

  /* ── Add track helper ── */
  const addTrack = (name, buffer, startTime = 0, opts = {}) => {
    setTracks(p => [...p, {
      ...DT,
      name,
      buffer,
      file: null,
      startTime,
      // Auto-Fit ON: assume the source matches the timeline at load time.
      // When timeline BPM/Key change, the track auto-stretches/pitches accordingly.
      // Auto-Fit OFF: leave sourceBpm/sourceKey null = play at original speed and pitch.
      sourceBpm: opts.sourceBpm != null ? opts.sourceBpm : (autoFit ? bpm : null),
      sourceKey: opts.sourceKey != null ? opts.sourceKey : (autoFit ? timelineKey : null),
    }]);
  };

  /* ── Listen for "Send to DAW" events from AI Music tab ── */
  useEffect(() => {
    const KEY_TO_INDEX = (k) => {
      if (!k) return null;
      // Strip minor "m" suffix; ACE-Step accepts both major/minor — for now we just take the root
      const root = k.replace(/m$/i, '').toUpperCase();
      const idx = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'].indexOf(root);
      return idx >= 0 ? idx : null;
    };
    const handler = async (e) => {
      const { url, name, bpm: tBpm, key: tKey } = e.detail || {};
      if (!url) return;
      try {
        const res = await fetch(url);
        const ab = await res.arrayBuffer();
        const buf = await getCtx().decodeAudioData(ab);
        const sourceBpm = tBpm || (autoFit ? bpm : null);
        const sourceKey = (tKey != null) ? KEY_TO_INDEX(tKey) : (autoFit ? timelineKey : null);
        addTrack(name || 'AI Track', buf, 0, { sourceBpm, sourceKey });
      } catch (err) {
        alert("Impossible de charger la piste AI dans le DAW: " + err.message);
      }
    };
    window.addEventListener('kazajah:send-to-daw', handler);
    return () => window.removeEventListener('kazajah:send-to-daw', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bpm, timelineKey, autoFit]);

  /* ── Import ── */
  const handleImport = async (e) => {
    for (const f of Array.from(e.target.files || [])) {
      try {
        const buf = await getCtx().decodeAudioData(await f.arrayBuffer());
        addTrack(f.name.replace(/\.[^.]+$/, ''), buf);
      } catch { alert(`Erreur: ${f.name}`); }
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  /* ── Stems ── */
  const handleStems = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setStemsLoading(true); setStemsProg(10);
    try {
      const ctx = getCtx();
      const ab = await ctx.decodeAudioData(await file.arrayBuffer());
      const sr = ab.sampleRate, len = ab.length, ch = ab.numberOfChannels;
      const filt = async (lo, hi, lbl) => {
        const oc = new OfflineAudioContext(ch, len, sr); const s = oc.createBufferSource(); s.buffer = ab; let n = s;
        if (lo > 0) { const h = oc.createBiquadFilter(); h.type = 'highpass'; h.frequency.value = lo; h.Q.value = 0.7; n.connect(h); n = h; }
        if (hi < 20000) { const l = oc.createBiquadFilter(); l.type = 'lowpass'; l.frequency.value = hi; l.Q.value = 0.7; n.connect(l); n = l; }
        n.connect(oc.destination); s.start(0); return { lbl, buf: await oc.startRendering() };
      };
      setStemsProg(25); const bass = await filt(0, 250, 'Bass');
      setStemsProg(50); const drums = await filt(250, 5000, 'Drums');
      setStemsProg(75); const vocals = await filt(300, 4000, 'Vocals');
      setStemsProg(95); const highs = await filt(5000, 20000, 'Highs');
      const base = file.name.replace(/\.[^.]+$/, '');
      [bass, drums, vocals, highs].forEach(s => addTrack(`${base} — ${s.lbl}`, s.buf));
      setPanel(null);
    } catch { alert("Erreur stems"); }
    setStemsLoading(false); setStemsProg(0);
    if (stemsRef.current) stemsRef.current.value = '';
  };

  /* ── Record ── */
  const toggleRec = async () => {
    if (recording) { recRef.current?.stop(); setRecording(false); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream); recChunks.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) recChunks.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const buf = await getCtx().decodeAudioData(await new Blob(recChunks.current, { type: 'audio/webm' }).arrayBuffer());
        addTrack(`Rec ${tracks.length + 1}`, buf, time);
      };
      rec.start(); recRef.current = rec; setRecording(true);
    } catch { alert("Micro non disponible"); }
  };

  /* ── AI Lyrics ── */
  const handleAI = async () => {
    if (!aiPrompt.trim() || aiLoading) return;
    setAiLoading(true); setAiResult('');
    try {
      const res = await fetch(`${API}/studio/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: aiPrompt, mode: 'lyrics' })
      });
      const data = await res.json();
      setAiResult(data.response || data.message || 'Pas de reponse');
    } catch { setAiResult('Erreur AI'); }
    setAiLoading(false);
  };

  /* ── AI Voice (TTS → track) ── */
  const handleVoice = async (text) => {
    if (!text.trim()) return;
    try {
      const res = await fetch(`${API}/studio/tts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: 'alloy' })
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const buf = await getCtx().decodeAudioData(await blob.arrayBuffer());
      addTrack(`AI Voice`, buf, time);
      setPanel(null);
    } catch { alert("Erreur TTS"); }
  };

  /* ── Split track at time ── */
  const splitTrackAt = (trackIdx, splitTime) => {
    const t = tracks[trackIdx]; if (!t?.buffer) return;
    const ctx = getCtx();
    const sr = t.buffer.sampleRate;
    const splitSample = Math.floor((splitTime - (t.startTime || 0)) * sr);
    if (splitSample <= 0 || splitSample >= t.buffer.length) return;

    // Left part
    const leftBuf = ctx.createBuffer(t.buffer.numberOfChannels, splitSample, sr);
    for (let ch = 0; ch < t.buffer.numberOfChannels; ch++) leftBuf.copyToChannel(t.buffer.getChannelData(ch).slice(0, splitSample), ch);

    // Right part
    const rightLen = t.buffer.length - splitSample;
    const rightBuf = ctx.createBuffer(t.buffer.numberOfChannels, rightLen, sr);
    for (let ch = 0; ch < t.buffer.numberOfChannels; ch++) rightBuf.copyToChannel(t.buffer.getChannelData(ch).slice(splitSample), ch);

    setTracks(p => {
      const next = [...p];
      next[trackIdx] = { ...t, buffer: leftBuf, name: t.name + ' (L)' };
      next.splice(trackIdx + 1, 0, { ...DT, name: t.name + ' (R)', buffer: rightBuf, startTime: splitTime });
      return next;
    });
    setSplitMode(false);
  };

  /* ── Delete selection (clip range OR region across tracks) ── */
  const deleteSelection = () => {
    // Region selection takes priority (applies to all overlapping tracks)
    if (regionSelection) {
      const { startSec, endSec } = regionSelection;
      const ctx = getCtx();
      setTracks(prev => prev.map(tr => {
        if (!tr.buffer) return tr;
        const trStart = tr.startTime || 0;
        const trEnd = trStart + tr.buffer.duration;
        if (endSec <= trStart || startSec >= trEnd) return tr;
        const sr = tr.buffer.sampleRate;
        const localStart = Math.max(0, startSec - trStart);
        const localEnd = Math.min(tr.buffer.duration, endSec - trStart);
        const s1 = Math.floor(localStart * sr);
        const s2 = Math.floor(localEnd * sr);
        if (s2 <= s1) return tr;
        const newLen = tr.buffer.length - (s2 - s1);
        if (newLen <= 0) return tr; // skip — would zero the track
        const nb = ctx.createBuffer(tr.buffer.numberOfChannels, newLen, sr);
        for (let ch = 0; ch < tr.buffer.numberOfChannels; ch++) {
          const src = tr.buffer.getChannelData(ch);
          const dst = nb.getChannelData(ch);
          dst.set(src.slice(0, s1), 0);
          dst.set(src.slice(s2), s1);
        }
        return { ...tr, buffer: nb };
      }));
      setRegionSelection(null);
      return;
    }

    if (!selection) return;
    const { trackIdx, startSec, endSec } = selection;
    const t = tracks[trackIdx]; if (!t?.buffer) return;
    const ctx = getCtx(); const sr = t.buffer.sampleRate;
    const s1 = Math.floor((startSec - (t.startTime || 0)) * sr);
    const s2 = Math.floor((endSec - (t.startTime || 0)) * sr);
    const newLen = t.buffer.length - (s2 - s1);
    if (newLen <= 0) { rmTrack(trackIdx); setSelection(null); return; }
    const newBuf = ctx.createBuffer(t.buffer.numberOfChannels, newLen, sr);
    for (let ch = 0; ch < t.buffer.numberOfChannels; ch++) {
      const src = t.buffer.getChannelData(ch);
      const dst = newBuf.getChannelData(ch);
      dst.set(src.slice(0, s1), 0);
      dst.set(src.slice(s2), s1);
    }
    setTracks(p => p.map((tr, i) => i === trackIdx ? { ...tr, buffer: newBuf } : tr));
    setSelection(null);
  };

  /* ── Eraser: silence a window around a click point (non-destructive length) ── */
  const eraseAt = (trackIdx, atSec) => {
    const t = tracks[trackIdx]; if (!t?.buffer) return;
    const ctx = getCtx();
    const sr = t.buffer.sampleRate;
    const halfWindow = eraseWindow / 2;
    const startSec = Math.max(0, atSec - (t.startTime || 0) - halfWindow);
    const endSec = Math.min(t.buffer.duration, atSec - (t.startTime || 0) + halfWindow);
    if (endSec <= startSec) return;
    const s1 = Math.floor(startSec * sr);
    const s2 = Math.floor(endSec * sr);
    const fadeLen = Math.min(Math.floor(0.005 * sr), Math.floor((s2 - s1) / 4)); // 5ms fade

    // Copy buffer to a new one so React detects the change
    const newBuf = ctx.createBuffer(t.buffer.numberOfChannels, t.buffer.length, sr);
    for (let ch = 0; ch < t.buffer.numberOfChannels; ch++) {
      const src = t.buffer.getChannelData(ch);
      const dst = newBuf.getChannelData(ch);
      dst.set(src);
      // Apply fade-in/out around the silence to avoid clicks
      for (let i = s1; i < s2; i++) {
        let g = 0;
        if (i - s1 < fadeLen) g = 1 - (i - s1) / fadeLen;
        else if (s2 - i < fadeLen) g = 1 - (s2 - i) / fadeLen;
        dst[i] = (src[i] || 0) * g;
      }
    }
    setTracks(p => p.map((tr, i) => i === trackIdx ? { ...tr, buffer: newBuf } : tr));
  };

  /* ── Play ── */
  const handlePlay = useCallback(() => {
    if (!tracks.length) return;
    const ctx = getCtx();
    srcsRef.current.forEach(s => { try { s.stop(); } catch {} }); srcsRef.current = [];
    const solo = tracks.some(t => t.solo);
    const mG = ctx.createGain(); mG.gain.value = master.loudness / 100;
    const lim = ctx.createDynamicsCompressor(); lim.threshold.value = -3 - (100 - master.limiter) * 0.3; lim.knee.value = 0; lim.ratio.value = 20;
    const eL = ctx.createBiquadFilter(); eL.type = 'lowshelf'; eL.frequency.value = 200; eL.gain.value = master.eq.low;
    const eM = ctx.createBiquadFilter(); eM.type = 'peaking'; eM.frequency.value = 1000; eM.Q.value = 1; eM.gain.value = master.eq.mid;
    const eH = ctx.createBiquadFilter(); eH.type = 'highshelf'; eH.frequency.value = 4000; eH.gain.value = master.eq.high;
    mG.connect(eL); eL.connect(eM); eM.connect(eH); eH.connect(lim); lim.connect(ctx.destination);

    tracks.forEach(t => {
      if (!t.buffer || (solo ? !t.solo : t.muted)) return;
      const src = ctx.createBufferSource(); src.buffer = t.buffer;
      const ratio = (t.sourceBpm && t.sourceBpm > 0) ? (bpm / t.sourceBpm) : 1;
      src.playbackRate.value = (tempo / 100) * ratio;
      const keyShift = (t.sourceKey != null) ? (timelineKey - t.sourceKey) : 0;
      src.detune.value = ((t.pitch || 0) + pitch + keyShift) * 100;
      const g = ctx.createGain(); g.gain.value = t.volume / 100;
      const p = ctx.createStereoPanner(); p.pan.value = t.pan / 100;
      let last = src;
      if (t.effects.reverb) { const cv = ctx.createConvolver(); const l = ctx.sampleRate * 1.5; const imp = ctx.createBuffer(2, l, ctx.sampleRate); for (let c = 0; c < 2; c++) { const d = imp.getChannelData(c); for (let j = 0; j < l; j++) d[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / l, 2); } cv.buffer = imp; const dry = ctx.createGain(); dry.gain.value = 0.7; const wet = ctx.createGain(); wet.gain.value = 0.3; const mx = ctx.createGain(); last.connect(dry); last.connect(cv); cv.connect(wet); dry.connect(mx); wet.connect(mx); last = mx; }
      if (t.effects.comp) { const c = ctx.createDynamicsCompressor(); c.threshold.value = -24; c.ratio.value = 4; last.connect(c); last = c; }
      if (t.effects.eq) { const e = ctx.createBiquadFilter(); e.type = 'peaking'; e.frequency.value = 2000; e.Q.value = 1; e.gain.value = 3; last.connect(e); last = e; }
      if (t.effects.delay) { const d = ctx.createDelay(1); d.delayTime.value = 60 / bpm / 2; const fb = ctx.createGain(); fb.gain.value = 0.3; const dG = ctx.createGain(); dG.gain.value = 0.7; const wG = ctx.createGain(); wG.gain.value = 0.3; const m = ctx.createGain(); last.connect(dG); last.connect(d); d.connect(fb); fb.connect(d); d.connect(wG); dG.connect(m); wG.connect(m); last = m; }
      last.connect(g); g.connect(p); p.connect(mG);
      const off = Math.max(0, time - (t.startTime || 0)); const when = Math.max(0, (t.startTime || 0) - time);
      src.start(ctx.currentTime + when, off); srcsRef.current.push(src);
    });
    t0Ref.current = ctx.currentTime - time; setPlaying(true);
    const tick = () => {
      const el = getCtx().currentTime - t0Ref.current;
      // Loop a region if defined
      if (loopEnabled && regionSelection && regionSelection.endSec - regionSelection.startSec > 0.05) {
        if (el >= regionSelection.endSec) {
          handleStop();
          setTime(regionSelection.startSec);
          setTimeout(() => handlePlay(), 30);
          return;
        }
        if (el < regionSelection.startSec) {
          setTime(regionSelection.startSec);
        }
      }
      if (el >= dur) {
        if (loopEnabled) {
          // Restart from 0 in loop mode
          handleStop();
          setTime(0);
          setTimeout(() => handlePlay(), 30);
          return;
        }
        setTime(dur);
        handleStop();
        return;
      }
      setTime(el);
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
  }, [tracks, time, master, pitch, tempo, bpm, timelineKey, dur, getCtx, loopEnabled, regionSelection]);

  const handleStop = useCallback(() => {
    srcsRef.current.forEach(s => { try { s.stop(); } catch {} }); srcsRef.current = [];
    cancelAnimationFrame(animRef.current); setPlaying(false);
  }, []);

  const rewind = () => { handleStop(); setTime(0); };

  /* ── Export ── */
  const handleExport = async () => {
    if (!tracks.length) return; setExporting(true);
    try {
      const sr = 44100, ns = Math.ceil(dur * sr);
      const oc = new OfflineAudioContext(2, ns, sr); const solo = tracks.some(t => t.solo);
      const mG = oc.createGain(); mG.gain.value = master.loudness / 100;
      const lim = oc.createDynamicsCompressor(); lim.threshold.value = -3; lim.ratio.value = 20;
      const eL = oc.createBiquadFilter(); eL.type = 'lowshelf'; eL.frequency.value = 200; eL.gain.value = master.eq.low;
      const eM = oc.createBiquadFilter(); eM.type = 'peaking'; eM.frequency.value = 1000; eM.gain.value = master.eq.mid;
      const eH = oc.createBiquadFilter(); eH.type = 'highshelf'; eH.frequency.value = 4000; eH.gain.value = master.eq.high;
      mG.connect(eL); eL.connect(eM); eM.connect(eH); eH.connect(lim); lim.connect(oc.destination);
      tracks.forEach(t => { if (!t.buffer || (solo ? !t.solo : t.muted)) return; const s = oc.createBufferSource(); s.buffer = t.buffer; const ratio = (t.sourceBpm && t.sourceBpm > 0) ? (bpm / t.sourceBpm) : 1; s.playbackRate.value = (tempo / 100) * ratio; const keyShift = (t.sourceKey != null) ? (timelineKey - t.sourceKey) : 0; s.detune.value = ((t.pitch || 0) + pitch + keyShift) * 100; const g = oc.createGain(); g.gain.value = t.volume / 100; const p = oc.createStereoPanner(); p.pan.value = t.pan / 100; s.connect(g); g.connect(p); p.connect(mG); s.start(t.startTime || 0); });
      const r = await oc.startRendering(); dlBlob(bufWav(r), 'kazajah-mix.wav');
    } catch (e) { alert("Erreur: " + e.message); }
    setExporting(false);
  };

  /* ── Track ops ── */
  const rmTrack = i => setTracks(p => p.filter((_, j) => j !== i));

  /** Duplicate the last track (or the selected one if any). The copy is inserted
   *  right after the original and offset on the timeline by its own duration so
   *  both clips can play back-to-back without overlap. */
  const duplicateTrack = useCallback(() => {
    setTracks(prev => {
      if (prev.length === 0) return prev;
      // Prefer the track from the active selection, else last track
      const targetIdx = selection?.trackIdx ?? (prev.length - 1);
      const orig = prev[targetIdx];
      if (!orig?.buffer) return prev;
      const ctx = getCtx();
      const sr = orig.buffer.sampleRate;
      const copyBuf = ctx.createBuffer(orig.buffer.numberOfChannels, orig.buffer.length, sr);
      for (let ch = 0; ch < orig.buffer.numberOfChannels; ch++) {
        copyBuf.copyToChannel(orig.buffer.getChannelData(ch).slice(), ch);
      }
      const copy = {
        ...orig,
        buffer: copyBuf,
        name: `${orig.name} (copy)`,
        startTime: (orig.startTime || 0) + orig.buffer.duration,
      };
      const next = [...prev];
      next.splice(targetIdx + 1, 0, copy);
      return next;
    });
  }, [selection, getCtx]);

  // Keep the ref in sync so the keyboard handler always calls the latest version
  useEffect(() => { duplicateTrackRef.current = duplicateTrack; }, [duplicateTrack]);
  const upd = (i, k, v) => setTracks(p => p.map((t, j) => j === i ? { ...t, [k]: v } : t));
  const togFx = (i, fx) => setTracks(p => p.map((t, j) => j === i ? { ...t, effects: { ...t.effects, [fx]: !t.effects[fx] } } : t));

  useEffect(() => () => handleStop(), [handleStop]);

  const seek = (e) => { if (playing) return; const r = tlRef.current?.getBoundingClientRect(); if (!r) return; const x = e.clientX - r.left + (tlRef.current?.scrollLeft || 0); setTime(Math.max(0, x / px)); };

  // ── Drag-to-select region (when SELECT tool is active) ─────────────────
  const regionDragRef = useRef(null);
  const handleRulerMouseDown = (e) => {
    if (!selectMode) return;
    e.preventDefault();
    const r = tlRef.current?.getBoundingClientRect(); if (!r) return;
    const startX = e.clientX;
    const startT = Math.max(0, (e.clientX - r.left + (tlRef.current?.scrollLeft || 0)) / px);
    regionDragRef.current = { startT };
    setRegionSelection({ startSec: startT, endSec: startT });

    const onMove = (ev) => {
      if (!regionDragRef.current) return;
      const t = Math.max(0, (ev.clientX - r.left + (tlRef.current?.scrollLeft || 0)) / px);
      const s = Math.min(regionDragRef.current.startT, t);
      const en = Math.max(regionDragRef.current.startT, t);
      setRegionSelection({ startSec: s, endSec: en });
    };
    const onUp = () => {
      regionDragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      // Discard tiny selections (treat as click)
      setRegionSelection(prev => (prev && (prev.endSec - prev.startSec) > 0.05) ? prev : null);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  const fmt = s => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}.${Math.floor((s % 1) * 10)}`;

  const handleTimelineClick = (e, trackIdx) => {
    const r = tlRef.current?.getBoundingClientRect(); if (!r) return;
    const clickTime = (e.clientX - r.left + (tlRef.current?.scrollLeft || 0)) / px;
    if (splitMode) {
      splitTrackAt(trackIdx, clickTime);
    } else if (eraseMode) {
      eraseAt(trackIdx, clickTime);
    }
  };

  return (
    <div className="bg-zinc-950 rounded-2xl border border-zinc-800 overflow-hidden" data-testid="kazajah-daw">
      {/* ═══ TOP BAR ═══ */}
      <div className="bg-zinc-900 border-b border-zinc-800 px-3 py-2 flex items-center gap-2 flex-wrap">
        <Disc3 size={16} className="text-orange-500" />
        <span className="text-sm font-black text-white">KAZAJAH DAW</span>
        <div className="h-5 w-px bg-zinc-700" />

        {/* Transport */}
        <button onClick={rewind} className="p-1.5 bg-zinc-800 rounded text-zinc-400 hover:text-white"><SkipBack size={14} /></button>
        <button onClick={playing ? handleStop : handlePlay} data-testid="daw-play"
          className={`px-3 py-1.5 rounded font-bold text-xs flex items-center gap-1 ${playing ? 'bg-red-500 text-white' : 'bg-orange-500 text-black'}`}>
          {playing ? <><Square size={12} /> STOP</> : <><Play size={12} /> PLAY</>}
        </button>
        <button onClick={toggleRec} className={`p-1.5 rounded ${recording ? 'bg-red-500 text-white animate-pulse' : 'bg-zinc-800 text-zinc-400 hover:text-red-400'}`}><Mic size={14} /></button>
        <div className="bg-black px-3 py-1 rounded font-mono text-orange-400 text-sm font-bold">{fmt(time)}</div>
        <div className="h-5 w-px bg-zinc-700" />

        {/* Undo / Redo */}
        <button onClick={undo} data-testid="daw-undo"
          title="Annuler (Ctrl+Z)"
          className="p-1.5 bg-zinc-800 rounded text-zinc-400 hover:text-white">
          <Undo2 size={14} />
        </button>
        <button onClick={redo} data-testid="daw-redo"
          title="Refaire (Ctrl+Shift+Z)"
          className="p-1.5 bg-zinc-800 rounded text-zinc-400 hover:text-white">
          <Redo2 size={14} />
        </button>
        <button onClick={duplicateTrack} data-testid="daw-duplicate"
          title="Dupliquer la derniere piste (Ctrl+D)"
          disabled={tracks.length === 0}
          className="px-3 py-2 bg-zinc-800 rounded text-zinc-300 text-xs font-bold hover:bg-blue-500/30 hover:text-blue-300 flex items-center gap-1.5 disabled:opacity-40">
          <Copy size={14} /> DUPLICATE
        </button>
        <div className="h-5 w-px bg-zinc-700" />

        {/* BPM / Tonalite / Speed / Pitch */}
        <Label t="BPM"><input type="number" min="40" max="300" value={bpm} onChange={e => setBpm(+e.target.value)} className="w-12 bg-black border border-zinc-800 rounded px-1 py-0.5 text-white text-[10px] text-center" /></Label>
        <Label t="Tonalite">
          <select value={timelineKey} onChange={e => setTimelineKey(+e.target.value)}
            className="bg-black border border-yellow-500/40 rounded px-1 py-0.5 text-yellow-400 text-[10px] font-bold cursor-pointer">
            {KEYS.map((k, i) => <option key={k} value={i}>{k}</option>)}
          </select>
        </Label>
        <button onClick={() => setAutoFit(v => !v)}
          title="Auto-ajuste les nouvelles pistes au BPM et a la tonalite de la timeline"
          className={`px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1 ${autoFit ? 'bg-yellow-500 text-black' : 'bg-zinc-800 text-zinc-400'}`}>
          AUTO-FIT
        </button>
        <Label t="Speed"><input type="range" min="50" max="200" value={tempo} onChange={e => setTempo(+e.target.value)} className="w-14 h-1 accent-yellow-500" /><span className="text-yellow-500 font-bold text-[10px]">{tempo}%</span></Label>
        <Label t="Pitch"><input type="range" min="-12" max="12" value={pitch} onChange={e => setPitch(+e.target.value)} className="w-14 h-1 accent-amber-500" /><span className="text-amber-400 font-bold text-[10px]">{pitch > 0 ? '+' : ''}{pitch}st</span></Label>
        <div className="flex-1" />

        {/* Tools — agrandis pour visibilité */}
        <Btn act={selectMode} size="lg" onClick={() => { setSelectMode(v => !v); if (!selectMode) { setSplitMode(false); setEraseMode(false); } }} icon={<Layers size={15} />} label="SELECT" color="purple" />
        <Btn act={splitMode} size="lg" onClick={() => { setSplitMode(v => !v); if (!splitMode) { setEraseMode(false); setSelectMode(false); } }} icon={<Scissors size={15} />} label="SPLIT" color="red" />
        <Btn act={eraseMode} size="lg" onClick={() => { setEraseMode(v => !v); if (!eraseMode) { setSplitMode(false); setSelectMode(false); } }} icon={<Eraser size={15} />} label="GOMME" color="pink" />
        <Btn act={loopEnabled} size="lg" onClick={() => setLoopEnabled(v => !v)} icon={<Repeat size={15} />} label="LOOP" color="cyan" />
        {(selection || regionSelection) && <Btn size="lg" onClick={deleteSelection} icon={<Trash2 size={15} />} label="DEL" color="red" />}
        <Btn size="lg" onClick={() => setTimelineUnit(u => u === 'bars' ? 'sec' : 'bars')} icon={<span className="text-xs font-bold">{timelineUnit === 'bars' ? '𝄞' : '⏱'}</span>} label={timelineUnit === 'bars' ? 'BARS' : 'SEC'} color="zinc" />
        <div className="h-5 w-px bg-zinc-700" />
        <label className="px-2 py-1 bg-zinc-800 text-zinc-300 rounded text-[10px] font-bold cursor-pointer hover:bg-zinc-700 flex items-center gap-1">
          <Upload size={11} /> IMPORT
          <input ref={fileRef} type="file" accept="audio/*" multiple className="hidden" onChange={handleImport} />
        </label>
        <Btn act={panel === 'stems'} onClick={() => setPanel(panel === 'stems' ? null : 'stems')} icon={<Layers size={11} />} label="STEMS" color="pink" />
        <Btn act={panel === 'ai'} onClick={() => setPanel(panel === 'ai' ? null : 'ai')} icon={<Wand2 size={11} />} label="LYRICS" color="emerald" />
        <Btn act={panel === 'aimusic'} onClick={() => setPanel(panel === 'aimusic' ? null : 'aimusic')} icon={<Music size={11} />} label="AI MUSIC" color="emerald" />
        <Btn act={panel === 'voice'} onClick={() => setPanel(panel === 'voice' ? null : 'voice')} icon={<MessageSquare size={11} />} label="VOICE" color="cyan" />
        <Btn act={panel === 'beats'} size="lg" onClick={() => setPanel(panel === 'beats' ? null : 'beats')} icon={<Keyboard size={15} />} label="BEAT MAKER" color="yellow" />
        <Btn act={panel === 'mystems'} size="lg" onClick={() => setPanel(panel === 'mystems' ? null : 'mystems')} icon={<Disc3 size={15} />} label="MES STEMS" color="amber" />
        <Btn act={panel === 'master'} onClick={() => setPanel(panel === 'master' ? null : 'master')} icon={<Zap size={11} />} label="MASTER" color="yellow" />
        <div className="h-5 w-px bg-zinc-700" />
        <button onClick={handleExport} disabled={exporting || !tracks.length} className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-[10px] font-bold disabled:opacity-30 flex items-center gap-1">
          {exporting ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} EXPORT WAV
        </button>
      </div>

      {/* ═══ PANELS ═══ */}
      <AnimatePresence>{panel && (
        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden border-b border-zinc-800">
          {panel === 'stems' && <StemsPanel loading={stemsLoading} prog={stemsProg} stemsRef={stemsRef} onImport={handleStems} />}
          {panel === 'master' && <MasterPanel master={master} onChange={setMaster} />}
          {panel === 'ai' && <AIPanel prompt={aiPrompt} setPrompt={setAiPrompt} loading={aiLoading} result={aiResult} onGenerate={handleAI} />}
          {panel === 'aimusic' && <AIMusicPanel bpm={bpm} timelineKey={timelineKey} keys={KEYS} />}
          {panel === 'voice' && <VoicePanel onGenerate={handleVoice} />}
          {panel === 'beats' && <BeatsPanel bpm={bpm} onAddToTimeline={addTrack} getCtx={getCtx} />}
          {panel === 'mystems' && <MyStemsPanel onAddToTimeline={addTrack} getCtx={getCtx} bpm={bpm} timelineKey={timelineKey} autoFit={autoFit} keys={KEYS} />}
        </motion.div>
      )}</AnimatePresence>

      {/* ═══ MAIN: Mixer + Timeline ═══ */}
      <div className="flex" style={{ height: tracks.length ? `${Math.max(240, tracks.length * 88 + 32)}px` : '280px' }}>
        {/* Mixer */}
        <div className="w-48 flex-shrink-0 bg-zinc-900 border-r border-zinc-800 overflow-y-auto">
          <div className="h-6 border-b border-zinc-800 px-2 flex items-center"><span className="text-[8px] text-zinc-600 uppercase font-bold tracking-widest">Mixer</span></div>
          {tracks.map((t, i) => <MixerStrip key={i} t={t} i={i} bpm={bpm} timelineKey={timelineKey} onRemove={rmTrack} onUpdate={upd} onToggleFx={togFx} />)}
          <label className="h-8 flex items-center justify-center gap-1 text-zinc-700 hover:text-yellow-400 cursor-pointer text-[10px]">
            <Plus size={10} /> Piste<input type="file" accept="audio/*" multiple className="hidden" onChange={handleImport} />
          </label>
        </div>

        {/* Timeline */}
        <div ref={tlRef} className="flex-1 overflow-auto bg-zinc-950 relative" onClick={(!splitMode && !eraseMode && !selectMode) ? seek : undefined}
          style={{ cursor: splitMode ? 'crosshair' : eraseMode ? 'cell' : selectMode ? 'col-resize' : 'default' }}>
          {/* Ruler — bars or seconds */}
          <div className="sticky top-0 z-20 h-6 bg-zinc-900/90 backdrop-blur-sm border-b border-zinc-800"
            onMouseDown={handleRulerMouseDown}
            style={{ cursor: selectMode ? 'col-resize' : 'default' }}>
            <svg width={dur * px} height="24">
              {timelineUnit === 'bars' ? (() => {
                // 1 bar = 4 beats; 1 beat = 60/bpm sec → 1 bar = 240/bpm sec
                const secPerBar = 240 / bpm;
                const totalBars = Math.ceil(dur / secPerBar);
                return Array.from({ length: totalBars }, (_, i) => {
                  const x = i * secPerBar * px;
                  return (
                    <g key={i}>
                      <line x1={x} y1="0" x2={x} y2="24" stroke="#444" strokeWidth="1" />
                      <text x={x + 3} y="14" fill="#888" fontSize="10" fontFamily="monospace" fontWeight="bold">{i + 1}</text>
                      {[1, 2, 3].map(b => (
                        <line key={b} x1={x + (b / 4) * secPerBar * px} y1="18" x2={x + (b / 4) * secPerBar * px} y2="24" stroke="#222" strokeWidth="1" />
                      ))}
                    </g>
                  );
                });
              })() : Array.from({ length: Math.ceil(dur) }, (_, i) => (
                <g key={i}>
                  <line x1={i * px} y1="0" x2={i * px} y2="24" stroke="#333" strokeWidth="1" />
                  <text x={i * px + 3} y="14" fill="#555" fontSize="9" fontFamily="monospace">{Math.floor(i / 60)}:{(i % 60).toString().padStart(2, '0')}</text>
                  {[1, 2, 3].map(b => <line key={b} x1={i * px + (b / 4) * px} y1="18" x2={i * px + (b / 4) * px} y2="24" stroke="#222" strokeWidth="1" />)}
                </g>
              ))}
            </svg>
          </div>

          {tracks.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Music size={36} className="text-zinc-800 mx-auto mb-2" />
                <p className="text-zinc-600 font-bold text-sm">Importez, enregistrez ou generez</p>
                <p className="text-zinc-700 text-[10px] mb-3">Audio, Stems, AI Voice, Beats</p>
                <label className="inline-flex items-center gap-1 px-4 py-2 bg-orange-500 text-black font-bold rounded-lg cursor-pointer text-xs">
                  <Upload size={14} /> Importer<input type="file" accept="audio/*" multiple className="hidden" onChange={handleImport} />
                </label>
              </div>
            </div>
          ) : (
            <div style={{ width: `${dur * px}px` }} onMouseDown={selectMode ? handleRulerMouseDown : undefined}>
              {tracks.map((t, i) => (
                <WaveBlock key={i} t={t} i={i} px={px} splitMode={splitMode} eraseMode={eraseMode}
                  onAction={(e) => handleTimelineClick(e, i)}
                  onDragMove={(newStartTime) => upd(i, 'startTime', Math.max(0, newStartTime))}
                  onCrossTrackDrop={(from, to) => {
                    setTracks(prev => {
                      if (to < 0 || to >= prev.length || from === to) return prev;
                      const next = [...prev];
                      const [moved] = next.splice(from, 1);
                      next.splice(to, 0, moved);
                      return next;
                    });
                  }} />
              ))}
            </div>
          )}

          {/* Region selection overlay (LOOP region + GOMME selection target) */}
          {regionSelection && (
            <div className="absolute top-6 bottom-7 z-10 pointer-events-none"
              data-testid="daw-region-selection"
              style={{
                left: `${regionSelection.startSec * px}px`,
                width: `${Math.max(2, (regionSelection.endSec - regionSelection.startSec) * px)}px`,
                background: loopEnabled ? 'rgba(34, 211, 238, 0.18)' : 'rgba(168, 85, 247, 0.18)',
                borderLeft: `2px solid ${loopEnabled ? '#22d3ee' : '#a855f7'}`,
                borderRight: `2px solid ${loopEnabled ? '#22d3ee' : '#a855f7'}`,
              }} />
          )}

          {/* Playhead */}
          <div className="absolute top-0 bottom-0 z-30 pointer-events-none" style={{ left: `${time * px}px` }}>
            <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent border-t-orange-500" />
            <div className="w-px h-full bg-orange-500 opacity-80" />
          </div>

          {/* Zoom + status */}
          <div className="sticky bottom-0 z-20 bg-zinc-900/80 backdrop-blur-sm border-t border-zinc-800 px-3 py-1 flex items-center gap-2 flex-wrap">
            <span className="text-[9px] text-zinc-600">Zoom</span>
            <input type="range" min="15" max="250" value={zoom} onChange={e => setZoom(+e.target.value)} className="w-24 h-1 accent-orange-500" />
            <span className="text-[9px] text-zinc-500">{tracks.length} pistes | {fmt(dur)}</span>
            {selectMode && <span className="text-[9px] text-purple-400 font-bold ml-2">MODE SELECT — Glisse sur la timeline pour délimiter une région</span>}
            {splitMode && <span className="text-[9px] text-red-400 font-bold ml-2">MODE SPLIT — Cliquez sur une piste pour couper</span>}
            {eraseMode && <span className="text-[9px] text-pink-400 font-bold ml-2">MODE GOMME — {regionSelection ? 'Bouton DEL pour effacer la sélection' : `Clic = effacer ${eraseWindow}s autour du clic`}</span>}
            {loopEnabled && <span className="text-[9px] text-cyan-400 font-bold ml-2">LOOP {regionSelection ? `— ${(regionSelection.endSec - regionSelection.startSec).toFixed(1)}s en boucle` : '— Timeline entière en boucle'}</span>}
            {regionSelection && (
              <button onClick={() => setRegionSelection(null)} data-testid="daw-clear-region"
                className="text-[9px] px-2 py-0.5 bg-zinc-800 rounded text-zinc-400 hover:text-white">
                ✕ Effacer sélection
              </button>
            )}
            {eraseMode && (
              <div className="ml-3 flex items-center gap-1">
                <span className="text-[9px] text-zinc-600">Fenêtre</span>
                <input type="range" min="0.1" max="3" step="0.1" value={eraseWindow}
                  onChange={e => setEraseWindow(+e.target.value)} className="w-16 h-1 accent-pink-500" />
                <span className="text-[9px] text-pink-400 font-bold">{eraseWindow.toFixed(1)}s</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══ Sub-components ══ */
function Label({ t, children }) {
  return <div className="flex items-center gap-1 text-[10px]"><span className="text-zinc-600">{t}</span>{children}</div>;
}

function Btn({ act, onClick, icon, label, color, size }) {
  const sizeCls = size === 'lg'
    ? 'px-3 py-2 text-xs gap-1.5'
    : 'px-2 py-1 text-[10px] gap-1';
  return <button onClick={onClick} className={`${sizeCls} rounded font-bold flex items-center ${act ? `bg-${color}-500/30 text-${color}-400 ring-1 ring-${color}-500/50` : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>{icon} {label}</button>;
}

function MixerStrip({ t, i, onRemove, onUpdate, onToggleFx, bpm, timelineKey }) {
  const col = COLORS[i % COLORS.length];
  const fitted = t.sourceBpm === bpm && t.sourceKey === timelineKey;
  return (
    <div className="h-[88px] border-b border-zinc-800 px-2 py-1 hover:bg-zinc-800/30">
      <div className="flex items-center justify-between mb-0.5">
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: col }} />
          <span className="text-[10px] font-bold text-white truncate">{t.name}</span>
        </div>
        <button onClick={() => onRemove(i)} className="p-0.5 text-zinc-700 hover:text-red-400"><Trash2 size={10} /></button>
      </div>
      <div className="flex items-center gap-1 mb-0.5">
        <button onClick={() => onUpdate(i, 'muted', !t.muted)} className={`px-1 py-0.5 rounded text-[8px] font-black ${t.muted ? 'bg-red-500/40 text-red-300' : 'bg-zinc-800 text-zinc-600'}`}>M</button>
        <button onClick={() => onUpdate(i, 'solo', !t.solo)} className={`px-1 py-0.5 rounded text-[8px] font-black ${t.solo ? 'bg-yellow-500/40 text-yellow-300' : 'bg-zinc-800 text-zinc-600'}`}>S</button>
        {['rv','dl','eq','cp'].map((fx, fi) => {
          const full = ['reverb','delay','eq','comp'][fi];
          return <button key={fx} onClick={() => onToggleFx(i, full)} className={`px-1 py-0.5 rounded text-[7px] font-bold uppercase ${t.effects[full] ? 'bg-yellow-500/30 text-yellow-400' : 'bg-zinc-800/50 text-zinc-700'}`}>{fx}</button>;
        })}
      </div>
      {/* Source BPM/Key (per-track origin — drives auto-stretch/pitch) */}
      <div className="flex items-center gap-1 mb-0.5">
        <span className="text-[8px] text-zinc-600 w-7">SRC</span>
        <input type="number" min="40" max="300" value={t.sourceBpm ?? ''} placeholder="BPM"
          onChange={e => { const v = e.target.value; onUpdate(i, 'sourceBpm', v === '' ? null : +v); }}
          className="w-10 bg-black border border-zinc-800 rounded px-0.5 py-0 text-yellow-400 text-[9px] text-center" title="BPM source de la piste" />
        <select value={t.sourceKey ?? ''} onChange={e => { const v = e.target.value; onUpdate(i, 'sourceKey', v === '' ? null : +v); }}
          className="bg-black border border-zinc-800 rounded px-0.5 py-0 text-yellow-400 text-[9px] cursor-pointer" title="Tonalite source">
          <option value="">--</option>
          {KEYS.map((k, ki) => <option key={k} value={ki}>{k}</option>)}
        </select>
        <button onClick={() => { onUpdate(i, 'sourceBpm', bpm); onUpdate(i, 'sourceKey', timelineKey); }}
          className={`px-1 rounded text-[7px] font-bold ${fitted ? 'bg-yellow-500 text-black' : 'bg-zinc-800 text-zinc-500 hover:text-yellow-400'}`}
          title="Caler sur la timeline">FIT</button>
      </div>
      <div className="flex items-center gap-1">
        <Volume2 size={8} className="text-zinc-600" />
        <input type="range" min="0" max="100" value={t.volume} onChange={e => onUpdate(i, 'volume', +e.target.value)} className="flex-1 h-0.5 accent-orange-500" />
        <input type="range" min="-100" max="100" value={t.pan} onChange={e => onUpdate(i, 'pan', +e.target.value)} className="w-8 h-0.5 accent-cyan-500" />
        <input type="range" min="-12" max="12" value={t.pitch || 0} onChange={e => onUpdate(i, 'pitch', +e.target.value)} className="w-8 h-0.5 accent-amber-500" title="Pitch fin" />
      </div>
    </div>
  );
}

function WaveBlock({ t, i, px, splitMode, eraseMode, onAction, onDragMove, onCrossTrackDrop }) {
  const ref = useRef(null);
  const col = COLORS[i % COLORS.length];
  const h = 60;
  const dragRef = useRef({ active: false, startX: 0, startY: 0, startT: 0, moved: false });

  useEffect(() => {
    const c = ref.current; if (!c || !t.buffer) return;
    const w = Math.max(50, Math.ceil(t.buffer.duration * px));
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = col + '15'; ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = col + 'bb';
    const data = t.buffer.getChannelData(0); const step = Math.ceil(data.length / w);
    for (let x = 0; x < w; x++) {
      let min = 1, max = -1;
      for (let j = 0; j < step; j++) { const v = data[x * step + j] || 0; if (v < min) min = v; if (v > max) max = v; }
      const y = ((1 + min) / 2) * h; const ht = Math.max(1, ((max - min) / 2) * h);
      ctx.fillRect(x, y, 1, ht);
    }
    ctx.fillStyle = '#fff'; ctx.font = 'bold 9px sans-serif'; ctx.fillText(t.name, 4, 11);
  }, [t.buffer, t.name, px, col]);

  const active = splitMode || eraseMode;
  const cursor = splitMode ? 'crosshair' : eraseMode ? 'cell' : 'grab';

  // ── Drag-to-move (disabled in split/erase modes) ─────────────────────
  const handleMouseDown = (e) => {
    if (active) return;
    e.stopPropagation();
    dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, startT: t.startTime || 0, moved: false, currentDy: 0 };
    document.body.style.cursor = 'grabbing';

    const trackRowHeight = h + 8;

    const onMove = (ev) => {
      if (!dragRef.current.active) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
      dragRef.current.currentDy = dy;
      const newT = dragRef.current.startT + dx / px;
      onDragMove?.(newT);
    };
    const onUp = () => {
      // Cross-track drop: if user dragged far vertically, propose to swap tracks
      const dy = dragRef.current.currentDy || 0;
      const rowsMoved = Math.round(dy / trackRowHeight);
      if (Math.abs(rowsMoved) >= 1 && onCrossTrackDrop) {
        onCrossTrackDrop(i, i + rowsMoved);
      }
      dragRef.current.active = false;
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleClickWrapper = (e) => {
    // Skip click if we just dragged
    if (dragRef.current.moved) {
      dragRef.current.moved = false;
      return;
    }
    if (active) onAction?.(e);
  };

  return (
    <div className="relative border-b border-zinc-800/50" style={{ height: `${h + 8}px`, paddingTop: '4px' }}
      onClick={handleClickWrapper}>
      <div className="absolute top-1 left-0 h-1 w-full opacity-20" style={{ background: col }} />
      <canvas ref={ref}
        onMouseDown={handleMouseDown}
        className="absolute rounded"
        style={{
          left: `${(t.startTime || 0) * px}px`,
          top: '4px',
          height: `${h}px`,
          opacity: t.muted ? 0.3 : 1,
          cursor: active ? cursor : 'grab',
        }} />
    </div>
  );
}

function StemsPanel({ loading, prog, stemsRef, onImport }) {
  return (
    <div className="px-4 py-3 bg-pink-500/5">
      <p className="text-xs font-bold text-pink-400 mb-1 flex items-center gap-1"><Layers size={12} /> STEMS SEPARATOR → Timeline</p>
      <p className="text-[10px] text-zinc-500 mb-2">Separe en 4 pistes: Bass, Drums, Vocals, Highs</p>
      {loading ? (
        <div className="flex items-center gap-2"><Loader2 size={14} className="animate-spin text-pink-400" /><div className="flex-1 h-1.5 bg-zinc-800 rounded-full"><div className="h-full bg-pink-500 rounded-full" style={{ width: `${prog}%` }} /></div><span className="text-[10px] text-pink-400">{prog}%</span></div>
      ) : (
        <label className="inline-flex items-center gap-1 px-3 py-1.5 bg-pink-500/20 text-pink-400 border border-pink-500/30 rounded text-xs font-bold cursor-pointer hover:bg-pink-500/30">
          <Upload size={12} /> Choisir un fichier<input ref={stemsRef} type="file" accept="audio/*" className="hidden" onChange={onImport} /></label>
      )}
    </div>
  );
}

function MasterPanel({ master, onChange }) {
  return (
    <div className="px-4 py-3 bg-yellow-500/5 grid grid-cols-4 gap-4">
      {[['Loudness', 'loudness', 0, 100, 'accent-red-500'], ['Limiter', 'limiter', 0, 100, 'accent-orange-500'], ['Stereo', 'stereoWidth', 0, 200, 'accent-cyan-500']].map(([l, k, mn, mx, ac]) => (
        <div key={k}><label className="text-[9px] text-zinc-500 uppercase">{l} <span className="text-zinc-300">{master[k]}%</span></label>
          <input type="range" min={mn} max={mx} value={master[k]} onChange={e => onChange({ ...master, [k]: +e.target.value })} className={`w-full h-1 ${ac}`} /></div>
      ))}
      <div><label className="text-[9px] text-zinc-500 uppercase">EQ</label>
        <div className="flex gap-2">{['low','mid','high'].map(b => (
          <div key={b} className="flex-1 text-center"><input type="range" min="-12" max="12" value={master.eq[b]} onChange={e => onChange({ ...master, eq: { ...master.eq, [b]: +e.target.value } })} className="w-full h-1 accent-emerald-500" /><span className="text-[8px] text-zinc-600">{b}</span></div>
        ))}</div></div>
    </div>
  );
}

function AIPanel({ prompt, setPrompt, loading, result, onGenerate }) {
  return (
    <div className="px-4 py-3 bg-emerald-500/5">
      <p className="text-xs font-bold text-emerald-400 mb-2 flex items-center gap-1"><Wand2 size={12} /> AI PAROLES & PRODUCTION</p>
      <div className="flex gap-2">
        <input value={prompt} onChange={e => setPrompt(e.target.value)} onKeyDown={e => e.key === 'Enter' && onGenerate()}
          placeholder="Ecris un refrain reggae, accords pour un zouk, remix house..." className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-1.5 text-white text-xs placeholder-zinc-500 focus:border-emerald-500 focus:outline-none" />
        <button onClick={onGenerate} disabled={loading} className="px-4 py-1.5 bg-emerald-500 text-black font-bold rounded text-xs disabled:opacity-40 flex items-center gap-1">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} Generer</button>
      </div>
      {result && <pre className="mt-2 p-2 bg-zinc-900 rounded text-xs text-zinc-300 whitespace-pre-wrap max-h-32 overflow-y-auto">{result}</pre>}
    </div>
  );
}

function VoicePanel({ onGenerate }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const handleGen = async () => { setLoading(true); await onGenerate(text); setLoading(false); };
  return (
    <div className="px-4 py-3 bg-cyan-500/5">
      <p className="text-xs font-bold text-cyan-400 mb-2 flex items-center gap-1"><MessageSquare size={12} /> AI VOICE → Timeline</p>
      <div className="flex gap-2">
        <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleGen()}
          placeholder="Texte a transformer en voix..." className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-1.5 text-white text-xs placeholder-zinc-500 focus:border-cyan-500 focus:outline-none" />
        <button onClick={handleGen} disabled={loading || !text.trim()} className="px-4 py-1.5 bg-cyan-500 text-black font-bold rounded text-xs disabled:opacity-40 flex items-center gap-1">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Mic size={12} />} Voix</button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// 9 RYTHMES TRADITIONNELS GWO KA — patterns 16 steps (1 bar en 4/4)
// Lignes : [Boula (basse), Markeur (lead), Ti-bwa (bambou), Chacha (shaker)]
// Sources : tradition orale, transcriptions de groupes du Léwoz contemporain.
// Chaque rythme a une "saveur" distincte ; à charger puis varier librement.
// ────────────────────────────────────────────────────────────────────────
const KA_PRESETS = {
  'Toumblak':  [ // festif, dansant — base de tout
    [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0], // Boula  : downbeat sur chaque temps
    [0,0,1,0, 0,1,0,1, 0,0,1,0, 0,1,0,1], // Markeur : syncopation festive
    [1,1,1,1, 1,1,1,1, 1,1,1,1, 1,1,1,1], // Ti-bwa  : flux continu (16ths)
    [0,0,0,1, 0,0,1,0, 0,0,0,1, 0,0,1,0], // Chacha  : offbeats roulants
  ],
  'Léwoz':     [ // combat, défi, rapide
    [1,0,0,1, 0,0,1,0, 0,1,0,0, 1,0,0,1], // Boula  : attaques décalées
    [0,1,0,0, 1,1,0,1, 0,0,1,0, 0,1,1,0], // Markeur : roulements intenses
    [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0], // Ti-bwa  : 8ths droits
    [1,0,0,1, 1,0,0,1, 1,0,0,1, 1,0,0,1], // Chacha  : marqueur d'urgence
  ],
  'Kaladja':   [ // lent, amour, mélancolie
    [1,0,0,0, 0,0,0,0, 0,0,1,0, 0,0,0,0], // Boula  : minimaliste, profond
    [0,0,0,0, 1,0,0,1, 0,0,0,0, 1,0,0,1], // Markeur : phrases espacées
    [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0], // Ti-bwa  : tempo lent stable
    [0,0,0,0, 0,0,1,0, 0,0,0,0, 0,0,1,0], // Chacha  : rare, doux
  ],
  'Padjanbel': [ // équilibré, polyvalent
    [1,0,0,1, 0,0,0,1, 0,0,0,1, 0,0,1,0], // Boula  : appui sur 1 puis variations
    [0,0,1,0, 0,1,0,0, 0,1,0,0, 1,0,0,1], // Markeur : conversation
    [1,1,0,1, 1,0,1,1, 0,1,1,0, 1,1,0,1], // Ti-bwa  : ride syncopée
    [0,0,0,1, 0,0,0,1, 0,0,0,1, 0,0,0,1], // Chacha  : marche stable
  ],
  'Graj':      [ // récolte, travail, énergique
    [1,0,0,0, 1,0,0,1, 0,0,1,0, 1,0,0,0], // Boula  : pulse de travail
    [0,1,0,1, 0,1,0,0, 1,0,0,1, 0,1,0,1], // Markeur : appels et réponses
    [1,1,1,1, 1,1,1,1, 1,1,1,1, 1,1,1,1], // Ti-bwa  : moteur
    [0,0,1,0, 0,0,1,0, 0,1,0,0, 0,1,0,0], // Chacha  : accentue les "et"
  ],
  'Woulé':     [ // "rouler" — drum roll style
    [1,0,1,0, 0,1,0,1, 0,1,0,0, 1,0,1,0], // Boula  : roule sur les temps
    [0,1,1,1, 0,1,1,1, 0,1,1,1, 0,1,1,1], // Markeur : roulements continus
    [0,1,1,0, 1,1,0,1, 0,1,1,0, 1,1,0,1], // Ti-bwa  : croches inversées
    [0,0,0,1, 0,0,0,1, 0,0,0,1, 0,0,0,1], // Chacha  : pulse principale
  ],
  'Mendé':     [ // cérémonial, lent, profond
    [1,0,0,1, 0,0,1,0, 1,0,0,1, 0,0,1,0], // Boula  : invocation
    [0,0,1,0, 1,0,0,1, 0,1,0,0, 1,0,0,1], // Markeur : prière percussive
    [1,1,1,1, 1,1,1,1, 1,1,1,1, 1,1,1,1], // Ti-bwa  : tempo respectueux
    [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0], // Chacha  : très espacé
  ],
  // ─── Bonus rythmes traditionnels ───────────────────────────────────────
  'Sobo':      [ // méditatif, lent, intériorisé
    [1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0], // Boula  : 2 frappes par mesure
    [0,0,1,0, 0,0,0,1, 0,0,1,0, 0,0,0,1], // Markeur : sparse
    [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0], // Ti-bwa  : sur les temps
    [0,0,0,0, 0,0,0,1, 0,0,0,0, 0,0,0,1], // Chacha  : minimal
  ],
  'Takouta':   [ // variation très rapide, virtuose
    [1,1,0,0, 1,0,1,1, 0,1,1,0, 1,1,0,1], // Boula  : double-hits
    [0,1,1,0, 1,1,0,1, 1,0,1,1, 0,1,1,0], // Markeur : très dense
    [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0], // Ti-bwa  : 8ths constants
    [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0], // Chacha  : sur les temps
  ],
};

function BeatsPanel({ bpm, onAddToTimeline, getCtx }) {
  const [kit, setKit] = useState('kazajah'); // 'kazajah' | 'gwoka' | 'custom'
  const [bars, setBars] = useState(4);
  const [volumeBoost, setVolumeBoost] = useState(1.5);
  const [previewing, setPreviewing] = useState(false);
  const [customSamples, setCustomSamples] = useState([]);
  const [customPacks, setCustomPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState(''); // pack_name filter for custom slots
  const [customSlots, setCustomSlots] = useState([null, null, null, null]);
  const [customBuffers, setCustomBuffers] = useState([null, null, null, null]);
  const previewRef = useRef(null);
  const stepsPerBar = 16;
  const totalSteps = bars * stepsPerBar;
  const [pattern, setPattern] = useState(() => Array(4).fill(null).map(() => Array(64).fill(false)));

  const INST_KAZAJAH = ['Kick', 'Snare', 'HiHat', 'Clap'];
  const INST_GWOKA = ['Boula', 'Markeur', 'Ti-bwa', 'Chacha'];
  const INST_LABEL = kit === 'gwoka' ? INST_GWOKA : kit === 'custom' ? ['Slot 1', 'Slot 2', 'Slot 3', 'Slot 4'] : INST_KAZAJAH;

  // Fetch custom packs + samples on mount + on kit change
  useEffect(() => {
    if (kit !== 'custom') return;
    const base = process.env.REACT_APP_BACKEND_URL;
    Promise.all([
      fetch(`${base}/api/beatmaker/samples`).then(r => r.json()).catch(() => []),
      fetch(`${base}/api/beatmaker/packs`).then(r => r.json()).catch(() => []),
    ]).then(([samples, packs]) => {
      setCustomSamples(Array.isArray(samples) ? samples : []);
      setCustomPacks(Array.isArray(packs) ? packs : []);
      if (!selectedPack && packs && packs.length > 0) setSelectedPack(packs[0].name);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kit]);

  // Samples filtered by selected pack (or all)
  const visibleSamples = selectedPack ? customSamples.filter(s => (s.pack_name || 'Library') === selectedPack) : customSamples;

  // Load buffer for any newly assigned custom sample
  useEffect(() => {
    if (kit !== 'custom') return;
    const ctx = getCtx();
    customSlots.forEach(async (id, idx) => {
      if (!id) {
        setCustomBuffers(prev => prev.map((b, i) => i === idx ? null : b));
        return;
      }
      const s = customSamples.find(x => x.id === id);
      if (!s) return;
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}${s.url}`;
        const resp = await fetch(url);
        const ab = await resp.arrayBuffer();
        const buf = await ctx.decodeAudioData(ab);
        setCustomBuffers(prev => prev.map((b, i) => i === idx ? buf : b));
      } catch (e) {
        console.warn('Failed to load custom sample', s.name, e);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customSlots, kit, customSamples]);

  useEffect(() => {
    setPattern(p => p.map(row => {
      if (row.length >= totalSteps) return row.slice(0, totalSteps);
      const nr = [...row];
      while (nr.length < totalSteps) nr.push(false);
      return nr;
    }));
  }, [totalSteps]);

  const toggleBeat = (inst, step) => {
    setPattern(p => p.map((row, i) => i === inst ? row.map((v, j) => j === step ? !v : v) : row));
  };

  const clearPattern = () => {
    setPattern(Array(4).fill(null).map(() => Array(totalSteps).fill(false)));
  };

  const loadPreset = (name) => {
    const preset = KA_PRESETS[name];
    if (!preset) return;
    setBars(1); // presets are 1 bar — user can manually extend after
    setKit('gwoka');
    setTimeout(() => {
      setPattern(preset.map(row => {
        const r = row.map(v => !!v);
        while (r.length < 64) r.push(false);
        return r;
      }));
    }, 60);
  };

  // ── Synth hits (Kazajah kit) ───────────────────────────────────────────
  const writeKazajahHit = (inst, atSample, L, R, sr, gain) => {
    if (inst === 0) {
      // KICK — clean 808-style: sub-bass body (60→40Hz) + tiny attack click.
      // Pas de noise → son ROND et propre, comme un kick de prod moderne.
      const dur = Math.floor(0.42 * sr);
      let phase = 0;
      for (let j = 0; j < dur && atSample + j < L.length; j++) {
        const t = j / sr;
        // Pitch envelope: 65Hz → 40Hz (warm sub)
        const f = 40 + 25 * Math.exp(-t * 22);
        phase += 2 * Math.PI * f / sr;
        // Soft attack click (sine 1.5kHz, decays in 5ms) — no noise
        const click = j < Math.floor(0.005 * sr)
          ? Math.sin(2 * Math.PI * 1500 * t) * (1 - j / (0.005 * sr)) * 0.18
          : 0;
        // Amplitude envelope: instant attack, smooth decay
        const env = Math.min(1, t / 0.001) * Math.exp(-t * 4.5);
        // Pure sine body for warmth (no harmonics that could buzz)
        const body = Math.sin(phase) * 1.0;
        const v = (body + click) * env;
        L[atSample + j] += v * gain * 1.1;
        R[atSample + j] += v * gain * 1.1;
      }
    } else if (inst === 1) {
      const dur = Math.floor(0.22 * sr);
      for (let j = 0; j < dur && atSample + j < L.length; j++) {
        const t = j / sr;
        const v = (Math.sin(2 * Math.PI * 200 * t) * 0.35 + (Math.random() * 2 - 1) * 0.65) * Math.min(1, t / 0.001) * Math.exp(-t * 18);
        L[atSample + j] += v * gain * 0.85; R[atSample + j] += v * gain * 0.85;
      }
    } else if (inst === 2) {
      const dur = Math.floor(0.05 * sr);
      let prev = 0;
      for (let j = 0; j < dur && atSample + j < L.length; j++) {
        const t = j / sr;
        const n = Math.random() * 2 - 1;
        const hp = n - prev * 0.92; prev = n;
        const v = hp * Math.min(1, t / 0.0005) * Math.exp(-t * 80);
        L[atSample + j] += v * gain * 0.55; R[atSample + j] += v * gain * 0.55;
      }
    } else if (inst === 3) {
      const totalDur = Math.floor(0.20 * sr);
      for (let h = 0; h < 4; h++) {
        const off = Math.floor(h * 0.008 * sr);
        const hitDur = Math.floor(0.04 * sr);
        for (let j = 0; j < hitDur && atSample + off + j < L.length; j++) {
          const t = j / sr;
          const v = (Math.random() * 2 - 1) * Math.exp(-t * 40);
          L[atSample + off + j] += v * gain * 0.65; R[atSample + off + j] += v * gain * 0.65 * (1 - h * 0.1);
        }
      }
      for (let j = 0; j < totalDur && atSample + j < L.length; j++) {
        const t = j / sr;
        if (t < 0.025) continue;
        const n = (Math.random() * 2 - 1) * Math.exp(-(t - 0.025) * 25);
        L[atSample + j] += n * gain * 0.18; R[atSample + j] += n * gain * 0.20;
      }
    }
  };

  // ── Synth hits (Gwo Ka kit) — version améliorée v2 ────────────────────
  // Plus de réalisme : skin resonance, harmoniques contrôlées, bandpass filter
  // pour Ti-bwa, double-hit pattern pour Markeur (cha-bom).
  const writeGwoKaHit = (inst, atSample, L, R, sr, gain) => {
    if (inst === 0) {
      // BOULA — gros tambour ka grave (rumble + skin slap court).
      // 2 modes mélangés : fundamental 60Hz + harmonique 90Hz pour le corps.
      const dur = Math.floor(0.70 * sr);
      let ph1 = 0, ph2 = 0;
      for (let j = 0; j < dur && atSample + j < L.length; j++) {
        const t = j / sr;
        // Pitch sweep réaliste : skin tension drops fast
        const f1 = 55 + 40 * Math.exp(-t * 8);   // sub
        const f2 = 90 + 50 * Math.exp(-t * 10);  // body
        ph1 += 2 * Math.PI * f1 / sr;
        ph2 += 2 * Math.PI * f2 / sr;
        // Skin slap (4ms filtered noise burst, doux pas brutal)
        let slap = 0;
        if (j < Math.floor(0.004 * sr)) {
          const n = (Math.random() * 2 - 1);
          slap = n * (1 - j / (0.004 * sr)) * 0.22;
        }
        // Amplitude envelope long decay
        const env = Math.min(1, t / 0.002) * Math.exp(-t * 3.2);
        const body = Math.sin(ph1) * 0.95 + Math.sin(ph2) * 0.35;
        const v = (body + slap) * env;
        L[atSample + j] += v * gain * 1.1; R[atSample + j] += v * gain * 1.1;
      }
    } else if (inst === 1) {
      // MARKEUR — ka aigu avec son "cha-bom" (open hit puis muffled).
      // Pitch plus haut, decay rapide, snap propre.
      const dur = Math.floor(0.28 * sr);
      let phase = 0;
      for (let j = 0; j < dur && atSample + j < L.length; j++) {
        const t = j / sr;
        const f = 130 + 50 * Math.exp(-t * 14);
        phase += 2 * Math.PI * f / sr;
        // Snap court (3ms sine 2.5kHz)
        const snap = j < Math.floor(0.003 * sr)
          ? Math.sin(2 * Math.PI * 2500 * t) * (1 - j / (0.003 * sr)) * 0.35
          : 0;
        // Skin resonance + harmonique
        const body = Math.sin(phase) * 0.75 + Math.sin(phase * 1.5) * 0.12;
        const env = Math.min(1, t / 0.001) * Math.exp(-t * 8);
        const v = (body + snap) * env;
        L[atSample + j] += v * gain * 0.95; R[atSample + j] += v * gain * 0.95;
      }
    } else if (inst === 2) {
      // TI-BWA — deux baguettes sur la coque, attaque sèche bandpass 2.5-3kHz.
      // Idéal pour le tempo et la ride traditionnelle.
      const dur = Math.floor(0.035 * sr);
      let phase = 0;
      // Generate filtered noise via bandpass-ish state
      let prev1 = 0, prev2 = 0;
      for (let j = 0; j < dur && atSample + j < L.length; j++) {
        const t = j / sr;
        const n = Math.random() * 2 - 1;
        // Bandpass approximation centré ~2700Hz
        const bp = (n - prev1 * 1.3 + prev2 * 0.55);
        prev2 = prev1; prev1 = n;
        // Add a tonal click for the "wood" character
        phase += 2 * Math.PI * 2700 / sr;
        const click = Math.sin(phase) * 0.15;
        const env = Math.min(1, t / 0.0002) * Math.exp(-t * 140);
        const v = (bp * 0.7 + click) * env;
        L[atSample + j] += v * gain * 0.6; R[atSample + j] += v * gain * 0.6;
      }
    } else if (inst === 3) {
      // CHACHA — shaker traditionnel : noise rafraichi 2x, enveloppe en cloche
      // pour mimer le grain de sable / graine dans la calebasse.
      const dur = Math.floor(0.09 * sr);
      let prev = 0;
      for (let j = 0; j < dur && atSample + j < L.length; j++) {
        const t = j / sr;
        const n = Math.random() * 2 - 1;
        // HP filter (cheap) pour son aigu
        const hp = n - prev * 0.82; prev = n;
        // Bell envelope: build-up rapide, peak à 30ms, decay
        const peak = 0.03;
        const env = Math.exp(-Math.pow((t - peak) / 0.025, 2));
        const v = hp * env;
        L[atSample + j] += v * gain * 0.55;
        R[atSample + j] += v * gain * 0.62; // stereo width
      }
    }
  };

  // ── Mix custom sample into the master buffer ──────────────────────────
  const writeCustomHit = (slotBuf, atSample, L, R, gain) => {
    if (!slotBuf) return;
    const sL = slotBuf.getChannelData(0);
    const sR = slotBuf.numberOfChannels > 1 ? slotBuf.getChannelData(1) : sL;
    for (let j = 0; j < sL.length && atSample + j < L.length; j++) {
      L[atSample + j] += sL[j] * gain;
      R[atSample + j] += sR[j] * gain;
    }
  };

  const buildBuffer = () => {
    const ctx = getCtx();
    const stepDur = 60 / bpm / 4;
    const totalDur = stepDur * totalSteps;
    const sr = ctx.sampleRate;
    const buf = ctx.createBuffer(2, Math.ceil(totalDur * sr), sr);
    const L = buf.getChannelData(0);
    const R = buf.getChannelData(1);

    pattern.forEach((row, inst) => {
      row.forEach((on, step) => {
        if (!on || step >= totalSteps) return;
        const startSample = Math.floor(step * stepDur * sr);
        const gain = volumeBoost * 0.9;
        if (kit === 'gwoka') writeGwoKaHit(inst, startSample, L, R, sr, gain);
        else if (kit === 'custom') writeCustomHit(customBuffers[inst], startSample, L, R, gain);
        else writeKazajahHit(inst, startSample, L, R, sr, gain);
      });
    });

    for (let i = 0; i < L.length; i++) { L[i] = Math.tanh(L[i]); R[i] = Math.tanh(R[i]); }
    return buf;
  };

  const renderToTimeline = () => {
    const kitLabel = kit === 'gwoka' ? 'KA' : kit === 'custom' ? 'Custom' : 'Beat';
    onAddToTimeline(`${kitLabel} ${bars}bars`, buildBuffer(), 0);
  };

  const previewBeat = () => {
    if (previewing) {
      try { previewRef.current?.stop(); } catch {}
      previewRef.current = null;
      setPreviewing(false);
      return;
    }
    const ctx = getCtx();
    const buf = buildBuffer();
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start();
    src.onended = () => { setPreviewing(false); previewRef.current = null; };
    previewRef.current = src;
    setPreviewing(true);
  };

  return (
    <div className="px-4 py-3 bg-yellow-500/5" data-testid="daw-beats-panel">
      {/* Row 1 — kit selector + presets + Library access */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <p className="text-sm font-bold text-yellow-400 flex items-center gap-1.5 mr-2"><Keyboard size={14} /> BEAT MAKER</p>
        <span className="text-[10px] text-zinc-500 uppercase font-bold">Kit</span>
        <button onClick={() => setKit('kazajah')} data-testid="beatmaker-kit-kazajah"
          className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${kit === 'kazajah' ? 'bg-yellow-500 text-black ring-2 ring-yellow-400' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}>
          KAZAJAH
        </button>
        <button onClick={() => setKit('gwoka')} data-testid="beatmaker-kit-gwoka"
          className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${kit === 'gwoka' ? 'bg-orange-500 text-black ring-2 ring-orange-400' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}>
          🇬🇵 GWO KA
        </button>
        <button onClick={() => setKit('custom')} data-testid="beatmaker-kit-custom"
          className={`px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1.5 ${kit === 'custom' ? 'bg-cyan-500 text-black ring-2 ring-cyan-400' : 'bg-cyan-700/40 text-cyan-200 hover:bg-cyan-600/50'}`}>
          🎁 PACKS LIBRARY
        </button>
        <div className="h-5 w-px bg-zinc-700 mx-1" />
        <span className="text-[10px] text-zinc-500 uppercase font-bold">Preset Ka</span>
        <select onChange={(e) => { if (e.target.value) { loadPreset(e.target.value); e.target.value = ''; } }} data-testid="beatmaker-preset-select"
          value=""
          className="bg-zinc-900 border border-zinc-800 rounded text-xs text-white px-2 py-1.5 font-bold">
          <option value="">— Charger un rythme —</option>
          {Object.keys(KA_PRESETS).map(name => <option key={name} value={name}>{name}</option>)}
        </select>
      </div>

      {/* Row 2 — bars / volume / preview / actions */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] text-zinc-500 uppercase font-bold">Bars</span>
          {[4, 8, 16].map(n => (
            <button key={n} onClick={() => setBars(n)} data-testid={`beatmaker-bars-${n}`}
              className={`px-2.5 py-1.5 rounded text-xs font-bold transition-colors ${bars === n ? 'bg-yellow-500 text-black' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}>
              {n}
            </button>
          ))}
          <div className="h-4 w-px bg-zinc-700 mx-1" />
          <span className="text-[10px] text-zinc-500 uppercase font-bold">Vol</span>
          <input type="range" min="0.5" max="3.0" step="0.1" value={volumeBoost}
            onChange={e => setVolumeBoost(+e.target.value)} data-testid="beatmaker-volume"
            className="w-24 h-1.5 accent-yellow-500" />
          <span className="text-yellow-400 font-bold text-xs w-12 text-right">{Math.round(volumeBoost * 100)}%</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={previewBeat} data-testid="beatmaker-preview"
            title="Ecouter sans ajouter a la timeline"
            className={`px-4 py-2 rounded text-sm font-bold flex items-center gap-1.5 transition-colors shadow-lg ${previewing ? 'bg-emerald-500 text-black ring-2 ring-emerald-300 animate-pulse' : 'bg-emerald-500 text-black hover:bg-emerald-400'}`}>
            {previewing ? <><Square size={13} /> STOP</> : <><Play size={13} /> PLAY</>}
          </button>
          <button onClick={clearPattern} data-testid="beatmaker-clear"
            className="px-3 py-2 bg-zinc-800 text-zinc-300 rounded text-xs font-bold hover:bg-zinc-700">
            CLR
          </button>
          <button onClick={renderToTimeline} data-testid="beatmaker-add-to-timeline"
            className="px-4 py-2 bg-yellow-500 text-black font-bold rounded text-sm flex items-center gap-1.5 hover:bg-yellow-400 shadow-lg">
            <Plus size={14} /> Ajouter Timeline
          </button>
        </div>
      </div>

      {/* CUSTOM kit slot selectors */}
      {kit === 'custom' && (
        <div className="mb-3 space-y-2">
          {/* Pack picker */}
          {customPacks.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[9px] text-zinc-500 uppercase font-bold">Pack</span>
              <select data-testid="beatmaker-custom-pack" value={selectedPack}
                onChange={(e) => { setSelectedPack(e.target.value); setCustomSlots([null, null, null, null]); }}
                className="bg-zinc-900 border border-zinc-800 rounded text-[10px] text-white px-2 py-1 min-w-[180px]">
                {customPacks.map(p => (
                  <option key={p.name} value={p.name}>{p.name} ({p.count})</option>
                ))}
              </select>
              {customPacks.find(p => p.name === selectedPack)?.preview_url && (
                <audio controls src={`${process.env.REACT_APP_BACKEND_URL}${customPacks.find(p => p.name === selectedPack)?.preview_url}`}
                  className="h-7 max-w-[200px]" data-testid="beatmaker-pack-preview" />
              )}
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-1.5">
            {[0, 1, 2, 3].map(slot => (
              <select key={slot}
                data-testid={`beatmaker-custom-slot-${slot}`}
                value={customSlots[slot] || ''}
                onChange={e => setCustomSlots(prev => prev.map((v, i) => i === slot ? (e.target.value || null) : v))}
                className="bg-zinc-900 border border-zinc-800 rounded text-[10px] text-white px-2 py-1">
                <option value="">— Slot {slot + 1} —</option>
                {visibleSamples.map(s => (
                  <option key={s.id} value={s.id}>{s.name}{s.slot !== 'free' ? ` (${s.slot})` : ''}</option>
                ))}
              </select>
            ))}
          </div>
        </div>
      )}
      {kit === 'custom' && customSamples.length === 0 && (
        <div className="mb-3 px-3 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded text-[10px] text-cyan-300">
          Aucun sample custom encore. L'admin peut en uploader ou créer un pack depuis un track radio dans le tableau de bord.
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="space-y-1 min-w-min">
          {INST_LABEL.map((name, inst) => (
            <div key={inst} className="flex items-center gap-0.5">
              <span className="text-[9px] text-zinc-500 w-12 text-right flex-shrink-0">{name}</span>
              {pattern[inst].slice(0, totalSteps).map((on, step) => {
                const isBeat = step % 4 === 0;
                const isBar = step % 16 === 0;
                return (
                  <button key={step} onClick={() => toggleBeat(inst, step)}
                    data-testid={`beatmaker-cell-${inst}-${step}`}
                    className={`flex-shrink-0 rounded-sm transition-colors ${
                      on ? 'bg-yellow-500'
                        : isBar ? 'bg-zinc-600'
                          : isBeat ? 'bg-zinc-700'
                            : 'bg-zinc-800'
                    } hover:bg-yellow-500/50`}
                    style={{ width: bars > 8 ? '14px' : bars > 4 ? '18px' : '22px', height: bars > 8 ? '14px' : bars > 4 ? '18px' : '22px' }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── AI Music Panel (compact, generates and adds to DAW directly) ── */
function AIMusicPanel({ bpm, timelineKey, keys }) {
  const [prompt, setPrompt] = useState('');
  const [genre, setGenre] = useState('Elektro Ka');
  const [duration, setDuration] = useState(30);
  const [generating, setGenerating] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  const [customerEmail, setCustomerEmail] = useState(() => {
    try { return localStorage.getItem('kazajah_ai_email') || ''; } catch { return ''; }
  });
  const [showPacks, setShowPacks] = useState(false);
  const [balance, setBalance] = useState(null);
  const timerRef = useRef(null);

  const PRESETS = [
    { l: 'Elektro Ka', p: 'Elektro Ka rhythm, gwo ka percussion, deep bass, tropical' },
    { l: 'House', p: 'Classic 90s house, piano stab, four-on-the-floor kick' },
    { l: 'Hip Hop', p: 'Boom bap hip hop beat, vinyl crackle, jazz sample' },
    { l: 'Reggae', p: 'Roots reggae, organ skank, walking bass' },
    { l: 'Afrobeat', p: 'Modern afrobeat, log drums, mellow synth' },
    { l: 'Trap', p: 'Dark trap beat, 808 sub bass, fast hi-hats' },
  ];

  const generate = async () => {
    setError('');
    if (!prompt.trim() && !genre) return setError('Donne un prompt ou un genre');
    setGenerating(true);
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-music/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: prompt.trim(),
          tags: genre,
          duration: Math.max(10, Math.min(duration, 240)),
          bpm,
          key: keys[timelineKey],
          instrumental: true,
          customer_email: customerEmail || undefined,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(data.detail || `Erreur ${r.status}`);
        if (r.status === 402 || r.status === 429) setShowPacks(true);
        return;
      }
      const t = data.track;
      if (data.credit_balance) setBalance(data.credit_balance);
      // Send the result straight into the DAW timeline
      window.dispatchEvent(new CustomEvent('kazajah:send-to-daw', {
        detail: {
          url: `${process.env.REACT_APP_BACKEND_URL}${t.audio_url}`,
          name: (t.prompt || t.tags || 'AI Music').slice(0, 30),
          bpm: t.bpm,
          key: t.key,
        }
      }));
      setPrompt('');
    } catch (e) {
      setError(e.message || 'Erreur reseau');
    } finally {
      setGenerating(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
  };

  return (
    <div className="px-4 py-3 bg-emerald-500/5" data-testid="daw-aimusic-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-emerald-400 flex items-center gap-1">
          <Music size={12} /> AI MUSIC GENERATOR
          <span className="ml-2 text-[9px] text-zinc-500 font-normal">
            Genere a {bpm} BPM en {keys[timelineKey]} et ajoute auto a la timeline
          </span>
        </p>
        <button onClick={() => setShowPacks(v => !v)} data-testid="daw-aimusic-show-packs"
          className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20">
          {showPacks ? '✕ Masquer' : '🎁 Crédits'}
        </button>
      </div>
      {/* AI Credits panel — compact balance row + (toggled) pack grid */}
      <div className="mb-2">
        <AICreditsPanel
          compact
          email={customerEmail}
          onEmailChange={(v) => { setCustomerEmail(v); try { localStorage.setItem('kazajah_ai_email', v); } catch {} }}
          onBalanceChange={setBalance}
          hidePurchaseUI={!showPacks}
        />
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {PRESETS.map(p => (
          <button key={p.l} onClick={() => { setPrompt(p.p); setGenre(p.l); }}
            className="px-2 py-0.5 bg-zinc-800 hover:bg-emerald-500/30 text-zinc-400 hover:text-emerald-300 text-[10px] rounded-full">
            {p.l}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
        <input value={prompt} onChange={e => setPrompt(e.target.value)}
          placeholder="Decris ton son..."
          className="md:col-span-7 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-xs focus:outline-none focus:border-emerald-500"
          data-testid="daw-aimusic-prompt" />
        <input type="number" min="10" max="240" value={duration}
          onChange={e => setDuration(parseInt(e.target.value) || 30)}
          className="md:col-span-2 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-xs focus:outline-none focus:border-emerald-500"
          placeholder="sec" />
        <button onClick={generate} disabled={generating}
          data-testid="daw-aimusic-generate"
          className="md:col-span-3 py-2 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-black font-black rounded-lg text-xs disabled:opacity-50 flex items-center justify-center gap-1">
          {generating ? <><Loader2 size={11} className="animate-spin" /> {elapsed}s</> : <><Sparkles size={11} /> GENERER & ENVOYER</>}
        </button>
      </div>
      {error && (
        <div className="mt-2 px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded text-[10px] text-red-300">
          {error}
        </div>
      )}
    </div>
  );
}

/* ── MES STEMS panel (Album packs purchased) ── */
function MyStemsPanel({ onAddToTimeline, getCtx, bpm, timelineKey, autoFit, keys }) {
  const EMAIL_LS = 'kazajah_ai_email';
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem(EMAIL_LS) || ''; } catch { return ''; }
  });
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStemId, setLoadingStemId] = useState(null);
  const [error, setError] = useState('');

  const fetchPacks = useCallback(async (mail) => {
    const m = (mail || '').trim().toLowerCase();
    if (!m || !m.includes('@')) { setPacks([]); return; }
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API}/album-packs/my?email=${encodeURIComponent(m)}`);
      const data = await r.json();
      setPacks(data.packs || []);
    } catch {
      setError('Impossible de charger tes packs');
      setPacks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPacks(email); }, [fetchPacks, email]);

  const updateEmail = (v) => {
    setEmail(v);
    try { localStorage.setItem(EMAIL_LS, v); } catch (_e) { /* localStorage unavailable */ }
  };

  const KEY_TO_INDEX = (k) => {
    if (!k) return null;
    const root = String(k).replace(/m$/i, '').toUpperCase();
    const idx = keys.indexOf(root);
    return idx >= 0 ? idx : null;
  };

  const handleLoadStem = async (stem) => {
    setLoadingStemId(stem.id);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}${stem.url}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Téléchargement échoué');
      const ab = await res.arrayBuffer();
      const buf = await getCtx().decodeAudioData(ab);
      const sourceBpm = stem.bpm || (autoFit ? bpm : null);
      const sourceKey = stem.key != null ? KEY_TO_INDEX(stem.key) : (autoFit ? timelineKey : null);
      onAddToTimeline(stem.name, buf, 0, { sourceBpm, sourceKey });
    } catch (e) {
      alert('Erreur chargement stem : ' + e.message);
    } finally {
      setLoadingStemId(null);
    }
  };

  return (
    <div className="px-4 py-3 bg-amber-500/5" data-testid="daw-mystems-panel">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <p className="text-sm font-bold text-amber-400 flex items-center gap-1.5">
          <Disc3 size={14} /> MES ALBUM PACKS
        </p>
        <span className="text-[10px] text-zinc-500">
          Stems débloqués via tes achats Boutique
        </span>
        <div className="flex items-center gap-2 ml-auto bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1">
          <Mail size={12} className="text-amber-400" />
          <input
            type="email"
            value={email}
            onChange={(e) => updateEmail(e.target.value)}
            placeholder="ton.email@exemple.com"
            data-testid="mystems-email-input"
            className="bg-transparent text-xs text-white placeholder:text-zinc-600 focus:outline-none w-48"
          />
        </div>
      </div>

      {!email || !email.includes('@') ? (
        <div className="rounded-lg border border-dashed border-amber-500/30 p-4 text-amber-300 text-xs">
          Renseigne ton email d&apos;achat ci-dessus pour voir les album packs et leurs stems.
        </div>
      ) : loading ? (
        <p className="text-zinc-500 text-xs flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Chargement…</p>
      ) : error ? (
        <p className="text-red-400 text-xs">{error}</p>
      ) : packs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-700 p-4 text-zinc-500 text-xs space-y-2" data-testid="mystems-empty">
          <p>Aucun album pack pour cet email.</p>
          <p>
            Tu peux acheter un Album Pack dans la{' '}
            <button
              onClick={() => window.location.assign('/shop')}
              className="text-amber-400 underline hover:text-amber-300 inline-flex items-center gap-1"
              data-testid="mystems-open-shop">
              <ShoppingCart size={11} /> Boutique
            </button>
            {' '}— les stems apparaissent ici une fois le paiement validé.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {packs.map(pack => (
            <div key={pack.id} className="rounded-xl border border-amber-500/20 bg-zinc-950" data-testid={`mystems-pack-${pack.id}`}>
              <div className="flex items-center gap-3 p-3 border-b border-zinc-800">
                {pack.cover_image ? (
                  <img src={pack.cover_image} alt={pack.name} className="w-10 h-10 rounded object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                    <Disc3 size={16} className="text-amber-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm truncate">{pack.name}</p>
                  <p className="text-[10px] text-zinc-500 truncate">{pack.artist || '—'} • {pack.stems?.length || 0} stems</p>
                </div>
              </div>
              <div className="p-2 space-y-1">
                {(pack.stems || []).length === 0 ? (
                  <p className="text-zinc-600 text-xs italic px-2 py-1">Aucun stem disponible pour ce pack.</p>
                ) : (
                  pack.stems.map(stem => (
                    <div key={stem.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-900/60">
                      <Music size={12} className="text-amber-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs font-semibold truncate">{stem.name}</p>
                        <p className="text-[10px] text-zinc-500 truncate">
                          {stem.slot || 'free'}
                          {stem.bpm ? ` • ${stem.bpm} BPM` : ''}
                          {stem.key ? ` • ${stem.key}` : ''}
                        </p>
                      </div>
                      <button
                        onClick={() => handleLoadStem(stem)}
                        disabled={loadingStemId === stem.id}
                        data-testid={`mystems-load-stem-${stem.id}`}
                        className="px-2 py-1 bg-amber-500 text-black rounded text-[10px] font-bold flex items-center gap-1 hover:bg-amber-400 disabled:opacity-50">
                        {loadingStemId === stem.id ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} DAW
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ── WAV ── */
function bufWav(buf) {
  const nCh = buf.numberOfChannels, sr = buf.sampleRate, ba = nCh * 2, dl = buf.length * ba, bl = 44 + dl;
  const ab = new ArrayBuffer(bl), v = new DataView(ab);
  const ws = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  ws(0, 'RIFF'); v.setUint32(4, bl - 8, true); ws(8, 'WAVE'); ws(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, nCh, true);
  v.setUint32(24, sr, true); v.setUint32(28, sr * ba, true); v.setUint16(32, ba, true);
  v.setUint16(34, 16, true); ws(36, 'data'); v.setUint32(40, dl, true);
  const chs = []; for (let c = 0; c < nCh; c++) chs.push(buf.getChannelData(c));
  let o = 44;
  for (let i = 0; i < buf.length; i++) for (let c = 0; c < nCh; c++) { const s = Math.max(-1, Math.min(1, chs[c][i])); v.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7FFF, true); o += 2; }
  return new Blob([ab], { type: 'audio/wav' });
}
function dlBlob(b, n) { const u = URL.createObjectURL(b), a = document.createElement('a'); a.href = u; a.download = n; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(u); }
